import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
	addDays,
	format,
	isSameDay,
	isToday,
	startOfWeek,
} from "date-fns";

import {
	CalendarSlot,
	Meeting,
} from "../../../types";
import { useListPatients } from "../../../store";
import { EditMeetingModal, MeetingInfoModal } from "../../meeting-management";
import WeeklyMeetingInfo from "./weekly-meeting-info";

interface WeeklyViewProps {
	meetingList: CalendarSlot[];
	date: Date;
	visibleWeekDays?: number[];
	startTime?: number;
	endTime?: number;
	isLoading?: boolean;
	onSelectTimeslot?: (start: Date, end: Date) => void;
}

interface PositionedMeeting {
	meeting: CalendarSlot;
	topPercent: number;
	heightPercent: number;
	bottomPercent: number;
	leftPercent: number;
	widthPercent: number;
}

const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 18;
const SLOT_MINUTES = 30;
const FULL_DAY_START_MINUTES = 0;
const FULL_DAY_END_MINUTES = 24 * 60;

const normalizeHour = (value: number | undefined, fallback: number): number => {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return fallback;
	}

	return Math.max(0, Math.min(24, value));
};

const formatSlotLabel = (minutesFromMidnight: number): string => {
	const hours24 = Math.floor(minutesFromMidnight / 60) % 24;
	const minutes = minutesFromMidnight % 60;
	const hours12 = ((hours24 + 11) % 12) + 1;
	const suffix = hours24 >= 12 ? "pm" : "am";

	if (minutes === 0) {
		return `${hours12}${suffix}`;
	}

	return `${hours12}:${String(minutes).padStart(2, "0")}${suffix}`;
};

const getPositionedMeetings = (
	meetings: CalendarSlot[],
	startMinutes: number,
	endMinutes: number,
): PositionedMeeting[] => {
	const visibleMeetings = meetings
		.map((meeting) => {
			const start = new Date(meeting.startTime);
			const end = new Date(meeting.endTime);
			const startInMinutes = start.getHours() * 60 + start.getMinutes();
			const endInMinutes = end.getHours() * 60 + end.getMinutes();
			const clippedStart = Math.max(startInMinutes, startMinutes);
			const clippedEnd = Math.min(endInMinutes, endMinutes);

			if (clippedEnd <= clippedStart) {
				return null;
			}

			return {
				meeting,
				clippedStart,
				clippedEnd,
			};
		})
		.filter((meeting): meeting is NonNullable<typeof meeting> => meeting !== null)
		.sort((left, right) => {
			if (left.clippedStart !== right.clippedStart) {
				return left.clippedStart - right.clippedStart;
			}

			return left.clippedEnd - right.clippedEnd;
		});

	const groups: typeof visibleMeetings[] = [];
	let currentGroup: typeof visibleMeetings = [];
	let currentGroupEnd = -1;

	for (const meeting of visibleMeetings) {
		if (currentGroup.length === 0 || meeting.clippedStart < currentGroupEnd) {
			currentGroup.push(meeting);
			currentGroupEnd = Math.max(currentGroupEnd, meeting.clippedEnd);
			continue;
		}

		groups.push(currentGroup);
		currentGroup = [meeting];
		currentGroupEnd = meeting.clippedEnd;
	}

	if (currentGroup.length > 0) {
		groups.push(currentGroup);
	}

	const visibleMinutes = endMinutes - startMinutes;

	return groups.flatMap((group) => {
		const active: Array<{ column: number; end: number }> = [];
		const groupLayouts: Array<{
			meeting: CalendarSlot;
			clippedStart: number;
			clippedEnd: number;
			column: number;
		}> = [];
		let maxColumns = 1;

		for (const item of group) {
			for (let index = active.length - 1; index >= 0; index -= 1) {
				if (active[index].end <= item.clippedStart) {
					active.splice(index, 1);
				}
			}

			let column = 0;
			while (active.some((activeMeeting) => activeMeeting.column === column)) {
				column += 1;
			}

			active.push({ column, end: item.clippedEnd });
			groupLayouts.push({
				meeting: item.meeting,
				clippedStart: item.clippedStart,
				clippedEnd: item.clippedEnd,
				column,
			});
			maxColumns = Math.max(maxColumns, active.length, column + 1);
		}

		return groupLayouts.map((layout) => {
			const topPercent = ((layout.clippedStart - startMinutes) / visibleMinutes) * 100;
			const heightPercent = ((layout.clippedEnd - layout.clippedStart) / visibleMinutes) * 100;
			const bottomPercent = 100 - (topPercent + heightPercent);
			const widthPercent = 100 / maxColumns;
			const leftPercent = layout.column * widthPercent;

			return {
				meeting: layout.meeting,
				topPercent,
				heightPercent,
				bottomPercent,
				leftPercent,
				widthPercent,
			};
		});
	});
};

const WeeklyView: React.FC<WeeklyViewProps> = ({
	meetingList,
	date,
	visibleWeekDays = [1, 2, 3, 4, 5],
	startTime = DEFAULT_START_HOUR,
	endTime = DEFAULT_END_HOUR,
	isLoading = false,
	onSelectTimeslot,
}) => {
	const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
	const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
	const [now, setNow] = useState<Date>(new Date());
	const [scrollbarWidth, setScrollbarWidth] = useState<number>(0);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const lastAppliedScrollKeyRef = useRef<string | null>(null);

	const { data: patients = [], isLoading: patientsLoading } = useListPatients();

	const selectedMeeting = useMemo(
		() => meetingList.find((meeting) => meeting.meetingId === selectedMeetingId) ?? null,
		[meetingList, selectedMeetingId],
	);

	const patientLabelById = useMemo(() => {
		return patients.reduce(
			(acc, patient) => {
				acc[patient.patientId] = patient.displayId;
				return acc;
			},
			{} as Record<string, string>,
		);
	}, [patients]);

	const initialStartHour = useMemo(
		() => normalizeHour(startTime, DEFAULT_START_HOUR),
		[startTime],
	);

	const initialEndHour = useMemo(() => {
		const normalizedEnd = normalizeHour(endTime, DEFAULT_END_HOUR);
		if (normalizedEnd <= initialStartHour) {
			return initialStartHour + 1;
		}

		return normalizedEnd;
	}, [endTime, initialStartHour]);

	const initialStartMinutes = useMemo(() => Math.round(initialStartHour * 60), [initialStartHour]);
	const initialEndMinutes = useMemo(() => Math.round(initialEndHour * 60), [initialEndHour]);
	const visibleWeekKey = useMemo(() => {
		return startOfWeek(date, { weekStartsOn: 0 }).toISOString();
	}, [date]);

	const defaultVisibleMinutes = initialEndMinutes - initialStartMinutes;
	const slotCount = Math.max(
		1,
		Math.ceil((FULL_DAY_END_MINUTES - FULL_DAY_START_MINUTES) / SLOT_MINUTES),
	);
	const fullDayHeightPercent = useMemo(
		() => (FULL_DAY_END_MINUTES / defaultVisibleMinutes) * 100,
		[defaultVisibleMinutes],
	);
	const initialScrollKey = `${visibleWeekKey}-${initialStartMinutes}-${initialEndMinutes}`;

	const timeSlots = useMemo(() => {
		return Array.from({ length: slotCount + 1 }, (_, index) => {
			const slotMinutes = Math.min(
				FULL_DAY_START_MINUTES + index * SLOT_MINUTES,
				FULL_DAY_END_MINUTES,
			);
			return {
				minutes: slotMinutes,
				label: formatSlotLabel(slotMinutes),
			};
		});
	}, [slotCount]);

	const timezoneLabel = useMemo(() => {
		const offsetMinutes = -new Date().getTimezoneOffset();
		const sign = offsetMinutes >= 0 ? "+" : "-";
		const absolute = Math.abs(offsetMinutes);
		const hours = Math.floor(absolute / 60);
		const minutes = absolute % 60;

		if (minutes === 0) {
			return `${sign}${hours}`;
		}

		return `${sign}${hours}.${String(minutes).padStart(2, "0")}`;
	}, []);

	const groupedByDay = useMemo(() => {
		const weekStart = startOfWeek(date, { weekStartsOn: 0 });
		const allDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
		const days = allDays.filter((day) => visibleWeekDays.includes(day.getDay()));

		return days.map((day) => {
			const dayMeetings = meetingList
				.filter((meeting) => isSameDay(new Date(meeting.startTime), day))
				.sort(
					(a, b) =>
						new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
				);

			return {
				day,
				positionedMeetings: getPositionedMeetings(
					dayMeetings,
					FULL_DAY_START_MINUTES,
					FULL_DAY_END_MINUTES,
				),
			};
		});
		}, [date, meetingList, visibleWeekDays]);

	const isLoadingData = isLoading || patientsLoading;

	const handleSelectMeeting = useCallback((meetingId: string) => {
		setSelectedMeetingId(meetingId);
	}, []);

	const handleCloseMeetingModal = useCallback(() => {
		setSelectedMeetingId(null);
	}, []);

	const handleOpenEditModal = useCallback((meeting: Meeting | null) => {
		setEditingMeeting(meeting);
	}, []);

	const handleCloseEditModal = useCallback(() => {
		setEditingMeeting(null);
	}, []);

	const handleTimeslotSelect = useCallback(
		(day: Date, slotMinutes: number) => {
			if (!onSelectTimeslot) {
				return;
			}

			const slotStart = new Date(day);
			slotStart.setHours(0, slotMinutes, 0, 0);

			if (slotStart <= now) {
				return;
			}

			const slotEnd = new Date(slotStart);
			slotEnd.setMinutes(slotEnd.getMinutes() + SLOT_MINUTES);

			onSelectTimeslot(slotStart, slotEnd);
		},
		[now, onSelectTimeslot],
	);

	const scrollToDefaultTimeRange = useCallback(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) {
			return;
		}

		const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
		const targetScrollTop =
			(initialStartMinutes / FULL_DAY_END_MINUTES) * scrollContainer.scrollHeight;

		scrollContainer.scrollTop = Math.min(targetScrollTop, maxScrollTop);
		lastAppliedScrollKeyRef.current = initialScrollKey;
	}, [initialScrollKey, initialStartMinutes]);

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setNow(new Date());
		}, 60 * 1000);

		return () => {
			window.clearInterval(intervalId);
		};
	}, []);

	useEffect(() => {
		if (isLoadingData) {
			lastAppliedScrollKeyRef.current = null;
		}
	}, [isLoadingData]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) {
			return;
		}

		const measureScrollbarWidth = () => {
			const nextWidth = Math.max(0, scrollContainer.offsetWidth - scrollContainer.clientWidth);
			setScrollbarWidth((previous) => (previous === nextWidth ? previous : nextWidth));
		};

		measureScrollbarWidth();
		window.addEventListener("resize", measureScrollbarWidth);

		return () => {
			window.removeEventListener("resize", measureScrollbarWidth);
		};
	}, [groupedByDay.length, isLoadingData]);

	useEffect(() => {
		if (isLoadingData || groupedByDay.length === 0) {
			return;
		}

		if (lastAppliedScrollKeyRef.current === initialScrollKey) {
			return;
		}

		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) {
			return;
		}

		const frameId = window.requestAnimationFrame(() => {
			scrollToDefaultTimeRange();
		});

		return () => {
			window.cancelAnimationFrame(frameId);
		};
	}, [groupedByDay.length, initialScrollKey, isLoadingData, scrollToDefaultTimeRange]);

	if (isLoadingData) {
		return (
			<Stack
				sx={{
					width: "100%",
					height: "100%",
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					overflowX: "hidden",
					boxSizing: "border-box",
					px: 2,
					pb: 1,
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						overflow: "hidden",
						border: "1px solid #DDD",
						boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
						backgroundColor: "white",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<CircularProgress />
				</Box>
			</Stack>
		);
	}

	if (groupedByDay.length === 0) {
		return (
			<Stack
				sx={{
					width: "100%",
					height: "100%",
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					overflowX: "hidden",
					boxSizing: "border-box",
					px: 2,
					pb: 1,
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						overflow: "hidden",
						border: "1px solid #DDD",
						boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
						backgroundColor: "white",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="h6" color="text.secondary">
						No days selected
					</Typography>
				</Box>
			</Stack>
		);
	}

	return (
		<Stack
			sx={{
				width: "100%",
				height: "100%",
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				overflowX: "hidden",
				boxSizing: "border-box",
				px: 2,
				pb: 1,
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
					borderRadius: 2,
					overflow: "hidden",
					border: "1px solid #DDD",
					boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
					backgroundColor: "white",
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: `64px minmax(0, 1fr)`,
						borderBottom: "1px solid #E6E6E6",
					}}
				>
					<Stack
						sx={{
							borderRight: "1px solid #EEE",
							alignItems: "center",
							justifyContent: "center",
							px: 0.5,
							py: 1,
						}}
					>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								fontSize: "0.72rem",
								fontWeight: 700,
								lineHeight: 1.1,
								textAlign: "center",
								wordBreak: "break-word",
							}}
						>
							{timezoneLabel}
						</Typography>
					</Stack>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: `repeat(${groupedByDay.length}, minmax(0, 1fr)) ${scrollbarWidth}px`,
							minWidth: 0,
						}}
					>
						{groupedByDay.map(({ day }) => (
							<Stack
								key={`header-${day.toISOString()}`}
								sx={{
									px: 1,
									py: 1,
									alignItems: "center",
									borderRight: "1px solid #EEE",
									backgroundColor: isToday(day) ? "#eaf6ff" : "#FAFAFA",
								}}
							>
								<Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
									{format(day, "EEE").toUpperCase()}
								</Typography>
								<Box
									sx={{
										mt: 0.5,
										width: 30,
										height: 30,
										borderRadius: "50%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: isToday(day) ? "#1976d2" : "#EDF1F5",
										color: isToday(day) ? "#FFF" : "#1E2A35",
									}}
								>
									<Typography
										sx={{
											fontSize: "0.95rem",
											fontWeight: 800,
											lineHeight: 1,
										}}
									>
										{format(day, "d")}
									</Typography>
								</Box>
							</Stack>
						))}
						<Box
							component="button"
							onClick={scrollToDefaultTimeRange}
							sx={{
								appearance: "none",
								border: "none",
								borderLeft: "1px solid #EEE",
								backgroundColor: "#FAFAFA",
								cursor: "pointer",
								p: 0,
								minWidth: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								transition: "background-color 0.15s ease-in-out",
								"&:hover": {
									backgroundColor: "#F0F4F8",
								},
							}}
							aria-label="Reset calendar scroll"
							title="Reset to default time range"
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{
									fontSize: "0.62rem",
									fontWeight: 700,
									letterSpacing: "0.12em",
									transform: "rotate(-90deg)",
									whiteSpace: "nowrap",
									userSelect: "none",
								}}
							>
								FOCUS
							</Typography>
						</Box>
					</Box>
				</Box>

				<Box
					ref={scrollContainerRef}
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						overflowX: "hidden",
						scrollbarGutter: "stable",
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: `64px repeat(${groupedByDay.length}, minmax(0, 1fr))`,
							height: `${fullDayHeightPercent}%`,
							minHeight: "100%",
						}}
					>
						<Stack sx={{ borderRight: "1px solid #EEE", minHeight: 0 }}>
							{timeSlots.slice(0, -1).map((slot, index) => (
								<Box
									key={`slot-${slot.minutes}`}
									sx={{
										flex: 1,
										minHeight: 0,
										borderTop: index === 0 ? "none" : "1px solid #F0F0F0",
										position: "relative",
									}}
								>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											position: "absolute",
											top: -8,
											left: 4,
											fontSize: "0.68rem",
										}}
									>
										{slot.label}
									</Typography>
								</Box>
							))}
						</Stack>

						{groupedByDay.map(({ day, positionedMeetings }) => (
							<Box
								key={`col-${day.toISOString()}`}
								sx={{
									position: "relative",
									borderRight: "1px solid #EEE",
									backgroundColor: isToday(day) ? "rgba(25, 118, 210, 0.04)" : "transparent",
								}}
							>
								<Stack sx={{ height: "100%", minHeight: 0 }}>
									{timeSlots.slice(0, -1).map((slot, index) => {
										const slotStart = new Date(day);
										slotStart.setHours(0, slot.minutes, 0, 0);
										const isFutureSlot = slotStart > now;

										return (
											<Box
												key={`line-${day.toISOString()}-${slot.minutes}`}
												onClick={() => handleTimeslotSelect(day, slot.minutes)}
												sx={{
													flex: 1,
													minHeight: 0,
													borderTop: index === 0 ? "none" : "1px solid #F0F0F0",
													cursor: onSelectTimeslot && isFutureSlot ? "pointer" : "default",
													transition: "background-color 0.15s ease-in-out",
													"&:hover": onSelectTimeslot && isFutureSlot
														? {
															backgroundColor: "rgba(25, 118, 210, 0.08)",
														}
														: undefined,
												}}
											/>
										);
									})}
								</Stack>

								{isToday(day) && (() => {
									const nowMinutes = now.getHours() * 60 + now.getMinutes();
									const topPercent = (nowMinutes / FULL_DAY_END_MINUTES) * 100;

									return (
										<Box
											sx={{
												position: "absolute",
												top: `calc(${topPercent}% - 1px)`,
												left: 0,
												right: 0,
												height: "2px",
												backgroundColor: "#1976d2",
												zIndex: 3,
												pointerEvents: "none",
											}}
										/>
									);
								})()}

								{positionedMeetings.map((positionedMeeting) => {
									const patientLabel = patientLabelById[positionedMeeting.meeting.patientId] || "Unknown";

									return (
										<WeeklyMeetingInfo
											key={`${positionedMeeting.meeting.meetingId}-${positionedMeeting.meeting.startTime}`}
											meeting={positionedMeeting.meeting}
											patientLabel={patientLabel}
											topPercent={positionedMeeting.topPercent}
											bottomPercent={positionedMeeting.bottomPercent}
											leftPercent={positionedMeeting.leftPercent}
											widthPercent={positionedMeeting.widthPercent}
											onSelectMeeting={handleSelectMeeting}
										/>
									);
								})}
							</Box>
						))}

					</Box>
				</Box>
			</Box>

			{selectedMeeting && (
				<MeetingInfoModal
					open={selectedMeeting != null}
					handleClose={handleCloseMeetingModal}
					meetingId={selectedMeeting.meetingId}
					onEditMeeting={handleOpenEditModal}
				/>
			)}

			{editingMeeting && (
				<EditMeetingModal
					open={editingMeeting != null}
					handleClose={handleCloseEditModal}
					meetingId={editingMeeting.meetingId}
				/>
			)}
		</Stack>
	);
};

export default WeeklyView;
